import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, fmtCurrencyCell, fmtNum, getExportDateRangeLabel, listFrom, useDrillAuth } from './drillHelpers';
import { OrdersShimmer } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop, Bone } from './reports/reportTokens';
import SectionError from './reports/SectionError';
import SectionEmpty from './reports/SectionEmpty';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchLiveByChannelRequest, fetchHourlySalesRequest, fetchOrderLogRequest, fetchOrderDetailRequest, clearOrderDetail, fetchChannelNamesRequest, fetchSnapshotDetailsRequest } from '../redux/reports/clickHouse/clickHouseActions';
import type { LiveByChannelItem, HourlySalesResponse, OrderLogItem, OrderDetailResponse, SnapshotDetailsResponse } from '../interface/clickHouseInterface';
import HourlyBarChart from './reports/components/charts/reusable/HourlyBarChart';
import { getSalesByChannel, todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
// import { useStoreTimeZone } from '../utils/dateTimeUtils';
import { isHistoricalReportDateFilter } from '../utils/reportDateFilter';
import { getOrderLogExportApi } from '../redux/reports/clickHouse/clickHouseApi';
import { getCurrencySymbol } from '../utils/currency';
import { orderLogToExportTable } from '../utils/exportUtils';
import { ReportSectionExportButton } from './reports/components/ReportExportButton';
import { useDrillHistoryBack } from '../hooks/useDrillHistoryBack';
import { OrderDetailSlider } from './reports/components/OrderDetailSlider';

const DrillOrders = ({ onBack, initialDateFilter, initialDate, orderSource }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; snapshotValues?: any; orderSource?: 'direct' | 'thirdparty' } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();
    const dispatch = useAppDispatch();
    const liveByChannel: LiveByChannelItem[] = useAppSelector((s) => s.clickHouse.liveByChannelSuccess);
    const liveByChannelLoading: boolean = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailure: boolean = useAppSelector((s) => s.clickHouse.liveByChannelFailure);
    const snapshotDetails: SnapshotDetailsResponse | null = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess);
    const snapshotDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    const hourlySalesData: HourlySalesResponse | null = useAppSelector((s) => s.clickHouse.hourlySalesData);
    const hourlySalesLoading: boolean = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailure: boolean = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot?drill=orders', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const { locationId, token, selectedDate: drillDate, dateFilter: drillDateFilter } = useDrillAuth();
    const today = useMemo(() => todayStr(), []);
    const yesterday = useMemo(() => yesterdayStr(), []);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? drillDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? drillDateFilter ?? 'Today');
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    const exportDateRangeLabel = getExportDateRangeLabel(activeDateFilter, dateRange);

    // Order Log (Today only)
    const orderLogOrders = useAppSelector((s) => s.clickHouse.orderLogOrders) as OrderLogItem[];
    const orderLogLoading = useAppSelector((s) => s.clickHouse.orderLogLoading);
    const orderLogFailure = useAppSelector((s) => s.clickHouse.orderLogFailure);
    const orderLogTotalElements = useAppSelector((s) => s.clickHouse.orderLogTotalElements);
    const orderLogTotalPages = useAppSelector((s) => s.clickHouse.orderLogTotalPages);
    const exportOrderLogLoading = useAppSelector((s) => s.clickHouse.exportOrderLogLoading);
    const channelNames = useAppSelector((s) => s.clickHouse.channelNames);
    const orderDetailData = useAppSelector((s) => s.clickHouse.orderDetailData) as OrderDetailResponse | null;
    const orderDetailLoading = useAppSelector((s) => s.clickHouse.orderDetailLoading);
    const orderDetailError = useAppSelector((s) => s.clickHouse.orderDetailError);

    // Order Log local state
    const [olPage, setOlPage] = useState(0);
    const OL_PAGE_SIZE = 5;
    const [olSearch, setOlSearch] = useState('');
    const [olDebouncedSearch, setOlDebouncedSearch] = useState('');
    const [olSelectedOrder, setOlSelectedOrder] = useState<any>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Stable close handler for the order-detail view
    const closeDetail = useCallback(() => {
        setOlSelectedOrder(null);
        dispatch(clearOrderDetail());
    }, [dispatch]);

    // Hardware / swipe back closes the order-detail before exiting the drill
    useDrillHistoryBack(!!olSelectedOrder, closeDetail);

    const [olFiltersOpen, setOlFiltersOpen] = useState(false);
    const [olOrderTypes, setOlOrderTypes] = useState<string[]>([]);
    const [olMinAmt, setOlMinAmt] = useState('');
    const [olMaxAmt, setOlMaxAmt] = useState('');
    const [olMinGuests, setOlMinGuests] = useState('');
    const [expandedOlItems, setExpandedOlItems] = useState<Set<number>>(new Set());

    const [loading, setLoading] = useState(true);
    const [channelList, setChannelList] = useState<any[]>([]);

    const isToday = activeDateFilter === 'Today';
    // ClickHouse date + channel-filter params — dates omitted for Today (live path), passed only for
    // historical ranges. orderSource mirrors the desktop drill so mobile matches the selected channel.
    const chDates: { startDate?: string; endDate?: string; orderSource?: 'direct' | 'thirdparty' } = {
        ...(isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
        ...(orderSource ? { orderSource } : {}),
    };

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Orders',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    useEffect(() => {
        if (!locationId) {
            setLoading(false);
            return;
        }
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        setLoading(true);

        dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, orderSource }));

        if (isToday) {
            // Embedded: the parent SnapshotScreen already dispatches snapshot-details with the
            // correct orderSource — re-fetching here without it would race the same Redux slice
            // and can clobber it with an un-scoped response. Only the standalone route fetches its own.
            if (!isEmbedded) dispatch(fetchSnapshotDetailsRequest(locationId, undefined, undefined, orderSource));
            dispatch(fetchLiveByChannelRequest({ locationId, ...chDates }));
            setChannelList([]);
            setLoading(false);
        } else {
            // Historical: snapshot-details (same API as desktop) + channel breakdown
            if (!isEmbedded) dispatch(fetchSnapshotDetailsRequest(locationId, dateRange.startDate, dateRange.endDate, orderSource));
            getSalesByChannel({ ...opts }).catch(() => null).then((r) => {
                setChannelList(Array.isArray(listFrom(r)) ? listFrom(r) : []);
                setLoading(false);
            });
        }
    }, [locationId, token, dateRange.startDate, dateRange.endDate, isToday, orderSource, isEmbedded]);

    // Order Log debounce
    useEffect(() => {
        const t = setTimeout(() => setOlDebouncedSearch(olSearch), 300);
        return () => clearTimeout(t);
    }, [olSearch]);

    // Reset Order Log page when filters, date or sales channel changes
    useEffect(() => { setOlPage(0); }, [olDebouncedSearch, olOrderTypes, olMinAmt, olMaxAmt, olMinGuests, dateRange.startDate, orderSource]); // eslint-disable-line react-hooks/exhaustive-deps

    // Date filter or global sales-channel change clears Order Log search + filters so they
    // don't leak into the new range/channel (the embedded drill stays mounted when the parent
    // SnapshotScreen's channel filter changes — orderSource arrives as a prop)
    useEffect(() => {
        setOlSearch('');
        setOlDebouncedSearch('');
        setOlOrderTypes([]);
        setOlMinAmt('');
        setOlMaxAmt('');
        setOlMinGuests('');
        setOlFiltersOpen(false);
        setOlSelectedOrder(null);
    }, [activeDateFilter, selectedDate, orderSource]);

    // Fetch channel names once (Order Type dropdown)
    useEffect(() => {
        if (!locationId) return;
        dispatch(fetchChannelNamesRequest(locationId));
    }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Order Log — always ClickHouse (Today + historical via startDate/endDate)
    useEffect(() => {
        if (!locationId) return;
        dispatch(fetchOrderLogRequest({
            locationId,
            page: olPage + 1,
            size: OL_PAGE_SIZE,
            sortBy: 'orderTime',
            sortDir: 'desc',
            search: olDebouncedSearch || undefined,
            channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined,
            minAmount: olMinAmt ? Number(olMinAmt) : undefined,
            maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined,
            guestCount: olMinGuests ? Number(olMinGuests) : undefined,
            ...chDates,
        }));
    }, [locationId, olPage, olDebouncedSearch, olOrderTypes, olMinAmt, olMaxAmt, olMinGuests, dateRange.startDate, dateRange.endDate, isToday, orderSource]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Order Detail when a row is selected
    useEffect(() => {
        if (!olSelectedOrder || !locationId) { dispatch(clearOrderDetail()); return; }
        const orderId = (olSelectedOrder as OrderLogItem).orderId ?? '';
        if (orderId) dispatch(fetchOrderDetailRequest({ locationId, orderId, ...(isHistoricalReportDateFilter(activeDateFilter) ? { type: 'historical' } : {}) }));
    }, [olSelectedOrder, locationId, activeDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Both Today and Historical → /sales/live/v2/snapshot-details (mirrors desktop exactly)
    const totalOrders = snapshotDetails?.orderCount != null
        ? Number(snapshotDetails.orderCount)
        : (snapshotDetailsLoading ? null : 0);
    const totalStr = fmtNum(totalOrders ?? 0);

    const avgOrder = snapshotDetails?.orderCount
        ? Number(snapshotDetails.netSales) / Number(snapshotDetails.orderCount)
        : 0;

    // Hourly Orders chart data — from /sales/live/v2/hourly-sales for both Today and historical
    const hourlyOrdersChartData: { label: string; value: number }[] = (() => {
        const hours = hourlySalesData?.hours ?? [];
        if (!hours.length) return [];
        return hours.map(h => {
            const hv = h.hourValue % 24;
            const disp = hv % 12 || 12;
            const suffix = hv >= 12 ? 'p' : 'a';
            return { label: `${disp}${suffix}`, value: h.orderCount };
        });
    })();
    const hourlyPeakVal = hourlyOrdersChartData.reduce((m, d) => Math.max(m, d.value), 0);
    const hourlyPeakIdx = hourlyPeakVal > 0 ? hourlyOrdersChartData.findIndex(d => d.value === hourlyPeakVal) : -1;
    const peakHour = hourlyPeakIdx >= 0
        ? hourlyOrdersChartData[hourlyPeakIdx].label.replace(/(\d+)a$/, '$1 AM').replace(/(\d+)p$/, '$1 PM')
        : '—';

    // Avg orders per hour — sum(operatingStartHour..operatingEndHour) / operatingHourCount
    const avgOrdersPerHour = (() => {
        const opHourCount = hourlySalesData?.operatingHourCount ?? 0;
        const opStart = hourlySalesData?.operatingStartHour ?? 0;
        const opEnd = hourlySalesData?.operatingEndHour ?? 23;
        const windowTotal = (hourlySalesData?.hours ?? [])
            .filter(h => h.hourValue >= opStart && h.hourValue <= opEnd)
            .reduce((s, h) => s + h.orderCount, 0);
        return opHourCount > 0 ? windowTotal / opHourCount : 0;
    })();

    // Channel data: Historical → getSalesByChannel (Today uses liveByChannel from Redux)
    const rawChannelList: any[] = channelList;

    // Match the desktop behaviour: keep the four primary channels individually
    // (Dine-in, In-store, Pickup, Delivery) and roll the rest into a single "3rd Party" bucket.
    const PRIMARY_CHANNELS_RE = /^(dinein|dine-in|dine in|instore|in-store|in store|pickup|pick-up|pick up|delivery)$/i;
    const resolvedChannelList: any[] = (() => {
        const primary: any[] = [];
        let othersCount = 0;
        let othersSales = 0;
        (rawChannelList || []).forEach((item: any) => {
            const name = String(item.channelName ?? item.channel ?? item.name ?? '').trim();
            const count = Number(item.orderCount ?? item.totalOrders ?? item.orders ?? item.count ?? 0) || 0;
            const sales = Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0;
            if (PRIMARY_CHANNELS_RE.test(name)) {
                primary.push(item);
            } else if (count > 0 || sales > 0) {
                othersCount += count;
                othersSales += sales;
            }
        });
        if (othersCount > 0 || othersSales > 0) {
            primary.push({ channelName: '3rd Party', orderCount: othersCount, totalSales: othersSales });
        }
        return primary;
    })();


    const KNOWN_CHANNELS = ['dinein', 'instore', 'pickup', 'delivery'];
    const channelRows = (() => {
        const rows: { c: string; n: number; p: number }[] = [];
        let thirdPartyN = 0;
        resolvedChannelList.forEach((ch: any) => {
            const rawName = (ch.channelName ?? ch.channel ?? ch.name ?? ch.typeName ?? '').toLowerCase().replace(/[\s_-]/g, '');
            const n = Number(ch.orderCount ?? ch.totalOrders ?? ch.orders ?? ch.count ?? 0) || 0;
            if (KNOWN_CHANNELS.includes(rawName)) {
                rows.push({ c: ch.channelName ?? ch.channel ?? ch.name ?? ch.typeName ?? '—', n, p: 0 });
            } else {
                thirdPartyN += n;
            }
        });
        if (thirdPartyN > 0) rows.push({ c: '3rd Party', n: thirdPartyN, p: 0 });
        const denom = rows.reduce((s, r) => s + r.n, 0);
        rows.forEach((r) => { r.p = denom > 0 ? (r.n / denom) * 100 : 0; });
        // Adjust last row so toFixed(2) values always sum to exactly 100
        if (rows.length > 1 && denom > 0) {
            const sumRounded = rows.slice(0, -1).reduce((s, r) => s + parseFloat(r.p.toFixed(2)), 0);
            rows[rows.length - 1].p = parseFloat((100 - sumRounded).toFixed(2));
        }
        return rows;
    })();

    const subTitle = `${totalStr} total`;

    const olActiveFilterCount = olOrderTypes.length + (olMinAmt ? 1 : 0) + (olMaxAmt ? 1 : 0) + (olMinGuests ? 1 : 0);
    const olAmtError = olMinAmt && olMaxAmt && Number(olMinAmt) >= Number(olMaxAmt) ? 'Min must be less than Max' : null;
    const olHasFilters = !!(olOrderTypes.length || olMinAmt || olMaxAmt || olMinGuests);
    const olClearFilters = () => { setOlOrderTypes([]); setOlMinAmt(''); setOlMaxAmt(''); setOlMinGuests(''); };

    const inner = loading ? <OrdersShimmer /> : (<>
        <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{snapshotDetailsLoading ? <Bone w={80} h={12} r={4} /> : subTitle}</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }} ref={scrollContainerRef}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {/* snapshot-details drives Total/Avg Order for Today AND historical — shimmer during any refetch (SSE-18) */}
                    {[
                        { l: 'Total', v: snapshotDetailsLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : totalStr },
                        { l: 'Peak Hour', v: hourlySalesLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : hourlySalesFailure ? '--' : peakHour },
                        { l: 'Avg/Hour', v: hourlySalesLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : hourlySalesFailure ? '--' : String(Math.round(avgOrdersPerHour)) },
                        { l: 'Avg Order', v: snapshotDetailsLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : fmtCurrency(avgOrder) },
                    ].map((k, i) => (
                        <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', boxShadow: C.shadow }}>
                            <div style={{ fontSize: 7, color: C.t3, textTransform: 'uppercase' }}>{k.l}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: k.l === 'Peak Hour' ? C.accent : C.t1, marginTop: 2 }}>{k.v}</div>
                        </div>
                    ))}
                </div>
                {/* Hourly Orders — /sales/live/v2/hourly-sales for both Today and historical */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: '14px 12px', marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Hourly Orders</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 8 }}>Order volume by hour</div>
                    <HourlyBarChart
                        data={hourlyOrdersChartData}
                        loading={hourlySalesLoading}
                        error={hourlySalesFailure}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, orderSource }))}
                        kpiName="hourly orders"
                        barColor={C.green}
                        chartHeight={80}
                        formatValue={(v) => String(Math.round(Number(v)))}
                        showCurrency={false}
                        minHeight={120}
                        avgOverride={avgOrdersPerHour}
                    />
                </div>
                {!isToday && (
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>By Channel</div>
                    {channelRows.length ? channelRows.map((ch, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < channelRows.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                            <span style={{ fontSize: 12, color: C.t1 }}>{ch.c}</span>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <span style={{ fontSize: 10, color: C.t3 }}>{Number(ch.p || 0).toFixed(2)}%</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: C.t1, width: 28, textAlign: 'right' }}>{ch.n}</span>
                            </div>
                        </div>
                    )) : (
                        <div style={{ padding: '12px 0', textAlign: 'center' }}>{loading ? <Bone w={80} h={11} r={4} style={{ margin: '0 auto' }} /> : <span style={{ fontSize: 11, color: C.t3 }}>No channel data</span>}</div>
                    )}
                </div>
                )}
                {isToday && (
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>By Channel</div>
                    {liveByChannelFailure ? (
                        <SectionError kpiName="by channel" onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDates }))} minHeight={120} />
                    ) : liveByChannelLoading ? (
                        <div>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 4 ? `1px solid ${C.divider}` : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Bone w={8} h={8} r={2} />
                                        <Bone w={60} h={12} r={4} />
                                    </div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <Bone w={36} h={10} r={4} />
                                        <Bone w={20} h={12} r={4} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : liveByChannel.length ? (() => {
                        const BY_CH_PRIMARIES = [
                            { pattern: /^in.?store$/i, color: C.accent },
                            { pattern: /^dine.?in$/i, color: '#E86B6B' },
                            { pattern: /^pick.?up$/i, color: C.orange },
                            { pattern: /^delivery$/i, color: C.green },
                        ];
                        const grouped: { channel: string; orderCount: number; color: string }[] = [];
                        let thirdPartyCount = 0;
                        BY_CH_PRIMARIES.forEach(({ pattern, color }) => {
                            const match = liveByChannel.find(item => pattern.test(item.channel));
                            if (match && match.orderCount > 0) grouped.push({ channel: match.channel, orderCount: match.orderCount, color });
                        });
                        liveByChannel.forEach(item => {
                            if (!BY_CH_PRIMARIES.some(({ pattern }) => pattern.test(item.channel))) thirdPartyCount += item.orderCount;
                        });
                        if (thirdPartyCount > 0) grouped.push({ channel: '3rd Party', orderCount: thirdPartyCount, color: C.t3 });
                        if (!grouped.length) return <SectionEmpty kpiName="by channel" minHeight={120} />;
                        const chTotal = grouped.reduce((sum, item) => sum + item.orderCount, 0);
                        const pcts = (() => {
                            const raw = grouped.map(d => chTotal > 0 ? parseFloat(((d.orderCount / chTotal) * 100).toFixed(2)) : 0);
                            const diff = parseFloat((100 - raw.reduce((s, p) => s + p, 0)).toFixed(2));
                            if (diff !== 0) { const mi = raw.reduce((mi, p, i, a) => p > a[mi] ? i : mi, 0); raw[mi] = parseFloat((raw[mi] + diff).toFixed(2)); }
                            return raw;
                        })();
                        return grouped.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < grouped.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color }} />
                                    <span style={{ fontSize: 12, color: C.t1 }}>{item.channel}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <span style={{ fontSize: 10, color: C.t3 }}>{pcts[i].toFixed(2)}%</span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: C.t1, width: 28, textAlign: 'right' }}>{fmtNum(item.orderCount)}</span>
                                </div>
                            </div>
                        ));
                    })() : (
                        <SectionEmpty kpiName="by channel" minHeight={120} />
                    )}
                </div>
                )}

                {/* ── Order Log (Paid Orders) — ClickHouse (Today + historical via startDate/endDate) ── */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginTop: 12, boxShadow: C.shadow }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Order Log (Paid Orders)</div>
                            <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{isToday ? 'All paid orders for today' : 'All paid orders for the selected period'}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button
                                onClick={() => setOlFiltersOpen(true)}
                                style={{ position: 'relative', height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${olActiveFilterCount > 0 ? C.accent : C.cardBorder}`, background: olActiveFilterCount > 0 ? `${C.accent}15` : C.surface, color: olActiveFilterCount > 0 ? C.accent : C.t2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: 14 }}>filter_list</span>
                                Filters
                                {olActiveFilterCount > 0 && (
                                    <span style={{ position: 'absolute', top: -5, right: -5, width: 16, height: 16, borderRadius: 8, background: C.accent, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{olActiveFilterCount}</span>
                                )}
                            </button>
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title="Order Log (Paid Orders)"
                                filename={`order-log-paid-orders-${dateRange.startDate}`}
                                buildExport={async (format, opts) => {
                                    const loc = opts?.locationId ?? locationId;
                                    let size = orderLogTotalElements || 1;
                                    if (opts?.locationId) {
                                        const probe: any = await getOrderLogExportApi({ locationId: loc, page: 1, size: 1, sortBy: 'orderTime', sortDir: 'desc', search: olDebouncedSearch || undefined, channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined, guestCount: olMinGuests ? Number(olMinGuests) : undefined, minAmount: olMinAmt ? Number(olMinAmt) : undefined, maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined, ...chDates });
                                        size = Number(probe?.totalElements) || 1;
                                    }
                                    const res: any = await getOrderLogExportApi({ locationId: loc, page: 1, size, sortBy: 'orderTime', sortDir: 'desc', search: olDebouncedSearch || undefined, channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined, guestCount: olMinGuests ? Number(olMinGuests) : undefined, minAmount: olMinAmt ? Number(olMinAmt) : undefined, maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined, ...chDates });
                                    const orders = res?.orders ?? (Array.isArray(res) ? res : []);
                                    return orderLogToExportTable(orders, getCurrencySymbol(), format);
                                }}
                            />
                        </div>
                    </div>
                    {/* Search */}
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                        <input
                            value={olSearch}
                            onChange={e => setOlSearch(e.target.value)}
                            placeholder="Search order, customer, phone…"
                            className="order-log-search"
                            style={{ width: '100%', height: 30, padding: olSearch ? '0 26px 0 9px' : '0 9px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 11, color: C.t1, background: C.surface, outline: 'none', boxSizing: 'border-box' }}
                        />
                        {olSearch && (
                            <button onClick={() => setOlSearch('')} style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.t3 }}>
                                <span className="material-symbols-rounded" style={{ fontSize: 14 }}>close</span>
                            </button>
                        )}
                    </div>
                    {/* Total count */}
                    {orderLogTotalElements > 0 && !orderLogLoading && (
                        <div style={{ fontSize: 10, color: C.t3, marginBottom: 8 }}>{orderLogTotalElements} orders</div>
                    )}
                    {/* Table */}
                    {orderLogLoading ? (
                        <div>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${C.divider}` }}>
                                    <Bone w={null} h={12} r={4} mb={6} />
                                    <Bone w={'60%' as any} h={10} r={4} mb={4} />
                                    <Bone w={'40%' as any} h={10} r={4} />
                                </div>
                            ))}
                        </div>
                    ) : orderLogFailure ? (
                        <SectionError kpiName="order log" onRetry={() => dispatch(fetchOrderLogRequest({ locationId, page: olPage + 1, size: OL_PAGE_SIZE, sortBy: 'orderTime', sortDir: 'desc', search: olDebouncedSearch || undefined }))} minHeight={80} />
                    ) : orderLogOrders.length === 0 ? (
                        <SectionEmpty kpiName="paid orders" minHeight={80} />
                    ) : (
                        <>
                            {orderLogOrders.map((order: OrderLogItem, i: number) => {
                                const preview = order.itemPreview ?? '';
                                const isLong = preview.length > 35;
                                const expanded = expandedOlItems.has(i);
                                const toggleExpand = (e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setExpandedOlItems(prev => {
                                        const next = new Set(prev);
                                        next.has(i) ? next.delete(i) : next.add(i);
                                        return next;
                                    });
                                };
                                return (
                                    <div key={`${order.orderNo}-${i}`} onClick={() => setOlSelectedOrder(order)} style={{ padding: '9px 0', borderBottom: i < orderLogOrders.length - 1 ? `1px solid ${C.divider}` : 'none', cursor: 'pointer' }}>
                                        {/* Row 1: order# + amount */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                            <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>#{order.orderNo ?? '—'}</span>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{fmtCurrencyCell(Number(order.amount ?? 0))}</span>
                                        </div>
                                        {/* Row 2: customer name + phone | time right */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                            <div style={{ display: 'flex', gap: 5, alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                {order.customerName && <span style={{ fontSize: 10, color: C.t2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.customerName}</span>}
                                                {order.customerPhone && <span style={{ fontSize: 10, color: C.t3, flexShrink: 0 }}>{order.customerPhone}</span>}
                                            </div>
                                            <span style={{ fontSize: 10, color: C.t3, flexShrink: 0, marginLeft: 8 }}>{order.orderTime ?? '—'}</span>
                                        </div>
                                        {/* Row 3: order type */}
                                        <div style={{ fontSize: 10, color: C.t3, marginBottom: preview ? 2 : 0 }}>{order.orderType ?? '—'}</div>
                                        {/* Row 4: item preview (expandable) */}
                                        {preview && (
                                            <div style={{ fontSize: 10, color: C.t3 }}>
                                                <span>{expanded || !isLong ? preview : preview.slice(0, 35) + '…'}</span>
                                                {isLong && (
                                                    <button onClick={toggleExpand} style={{ background: 'none', border: 'none', color: C.accent, fontSize: 10, cursor: 'pointer', padding: '0 2px', marginLeft: 2 }}>
                                                        {expanded ? 'less' : 'more'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {/* Pagination */}
                            {orderLogTotalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                    <button onClick={() => setOlPage(p => Math.max(0, p - 1))} disabled={olPage === 0} style={{ padding: '3px 10px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: olPage === 0 ? C.hover : C.card, color: olPage === 0 ? C.t3 : C.t1, fontSize: 11, cursor: olPage === 0 ? 'default' : 'pointer' }}>← Prev</button>
                                    <span style={{ fontSize: 10, color: C.t2 }}>{olPage + 1} / {orderLogTotalPages}</span>
                                    <button onClick={() => setOlPage(p => Math.min(orderLogTotalPages - 1, p + 1))} disabled={olPage >= orderLogTotalPages - 1} style={{ padding: '3px 10px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: olPage >= orderLogTotalPages - 1 ? C.hover : C.card, color: olPage >= orderLogTotalPages - 1 ? C.t3 : C.t1, fontSize: 11, cursor: olPage >= orderLogTotalPages - 1 ? 'default' : 'pointer' }}>Next →</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
        </div>
    </>);

    // Full-screen filter panel — matches SalesScreen mobile pattern exactly
    const filterPanel = olFiltersOpen ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 800, background: C.surface, display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${C.divider}` }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: C.t1 }}>Filters</span>
                <button onClick={() => setOlFiltersOpen(false)} style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: C.hover, color: C.t2, cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {/* Order Type */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Order Type</div>
                    {(channelNames as string[]).filter(ch => !!ch).map(ot => (
                        <div key={ot} onClick={() => setOlOrderTypes(prev => prev.includes(ot) ? prev.filter(x => x !== ot) : [...prev, ot])} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.divider}`, cursor: 'pointer' }}>
                            <span style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${olOrderTypes.includes(ot) ? C.accent : C.t3}`, background: olOrderTypes.includes(ot) ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {olOrderTypes.includes(ot) && <span style={{ color: '#fff', fontSize: 10 }}>✓</span>}
                            </span>
                            <span style={{ fontSize: 13, color: C.t1 }}>{ot}</span>
                        </div>
                    ))}
                </div>
                {/* Amount Range */}
                <div style={{ marginBottom: olAmtError ? 4 : 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Amount Range</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            value={olMinAmt}
                            onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMinAmt(v); }}
                            placeholder="Min $"
                            type="number"
                            min="0"
                            style={{ flex: 1, height: 36, padding: '0 10px', border: `1px solid ${olAmtError ? C.red : C.cardBorder}`, borderRadius: 6, fontSize: 13, color: C.t1, background: C.surface }}
                        />
                        <input
                            value={olMaxAmt}
                            onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMaxAmt(v); }}
                            placeholder="Max $"
                            type="number"
                            min="0"
                            style={{ flex: 1, height: 36, padding: '0 10px', border: `1px solid ${olAmtError ? C.red : C.cardBorder}`, borderRadius: 6, fontSize: 13, color: C.t1, background: C.surface }}
                        />
                    </div>
                </div>
                {olAmtError && <div style={{ fontSize: 11, color: C.red, marginBottom: 12 }}>{olAmtError}</div>}
                {/* Guests ≥ */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Guests ≥</div>
                    <input
                        value={olMinGuests}
                        onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMinGuests(v); }}
                        placeholder="Min party size"
                        type="number"
                        min="0"
                        style={{ width: '100%', height: 36, padding: '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 13, color: C.t1, background: C.surface, boxSizing: 'border-box' }}
                    />
                </div>
                {olHasFilters && (
                    <button onClick={olClearFilters} style={{ width: '100%', height: 36, border: 'none', borderRadius: 6, fontSize: 13, color: C.accent, background: 'transparent', cursor: 'pointer', textDecoration: 'underline' }}>
                        Clear all filters
                    </button>
                )}
            </div>
            <div style={{ padding: 16, borderTop: `1px solid ${C.divider}` }}>
                <button onClick={() => setOlFiltersOpen(false)} style={{ width: '100%', height: 44, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: C.accent, cursor: 'pointer' }}>Apply Filters</button>
            </div>
        </div>
    ) : null;

    const orderDetailSlider = (
        <OrderDetailSlider
            visible={!!olSelectedOrder}
            onClose={closeDetail}
            orderNo={(olSelectedOrder as OrderLogItem)?.orderNo}
            orderType={(olSelectedOrder as OrderLogItem)?.orderType}
            loading={orderDetailLoading}
            error={orderDetailError}
            onRetry={() => olSelectedOrder && dispatch(fetchOrderDetailRequest({ locationId, orderId: (olSelectedOrder as OrderLogItem).orderId, ...(isHistoricalReportDateFilter(activeDateFilter) ? { type: 'historical' } : {}) }))}
            detail={orderDetailData}
        />
    );

    if (isEmbedded) return <>{filterPanel}{inner}{orderDetailSlider}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {filterPanel}
            {inner}
            {orderDetailSlider}
            <BottomNav />
        </Phone>
    );
};

export default DrillOrders;
